import {customRef, watch, Ref, ref, unref} from 'vue';

export function useDebounce () {

    const debounceRef = <T>(value: T, delay = 1000) => {
        let timer: number | ReturnType<typeof setTimeout> = null;
        return customRef((track, trigger) => {
            return {
                get() {
                    track();
                    return value;
                },
                set(newValue: T) {
                    clearTimeout(timer);
                    timer = setTimeout(() => {
                        value = newValue;
                        trigger();
                    }, delay);
                },
            };
        });
    };

    const dynamicDebounceRef = <T>(value: [T, number]) => {
        let timer: number | ReturnType<typeof setTimeout> = null;
        return customRef((track, trigger) => {
            return {
                get(): T {
                    track();
                    return value[0];
                },
                set(newValue: T | [T, number]) {
                    clearTimeout(timer);
                    const delay = newValue instanceof Array ? newValue[1] : value[1];
                    timer = setTimeout(() => {
                        value = [newValue instanceof Array ? newValue[0] : newValue, value[1]];
                        trigger();
                    }, delay);
                },
            };
        });
    };

    const debounceValue = <T>(value: Ref<T>, delay = 250): Ref<T> => {
        let timer: number | ReturnType<typeof setTimeout> = null;
        const state = ref<T>({...unref(value)}) as Ref<T>;
        watch(value, (newValue) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                state.value = {...newValue};
            }, delay);
        }, {deep: true});
        return state;
    };

    return {
        debounceRef,
        dynamicDebounceRef,
        debounceValue,
    };
}